/**
 * CompassStorefront — the public /compass sales page (approved mockup design).
 *
 * Shown to visitors and unpaid users. Entitled users (paid Bench members OR
 * standalone Compass subscribers — user.has_compass, computed server-side)
 * never see this page: the /compass route renders the full Compass tool
 * instead (see CompassGate in App.js).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock, Check, ArrowRight, Compass as CompassIcon, MapPin, Users,
  FileText, DollarSign, FlaskConical, Package, ShieldCheck, Rocket,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TemplateModal } from "@/pages/Compass";
import { TEMPLATES as COMPASS_TEMPLATES } from "@/compass/data";
import CopyDemoLinkButton from "@/components/CopyDemoLinkButton";

const PHASES = [
  { num: 1, icon: FileText, title: "Concept brief", desc: "Define your product and target market" },
  { num: 2, icon: FlaskConical, title: "Formulation", desc: "Build and test your recipe for scale" },
  { num: 3, icon: DollarSign, title: "Costing", desc: "Calculate margins and pricing strategy" },
  { num: 4, icon: Users, title: "Testing", desc: "Validate safety, shelf life, and quality" },
  { num: 5, icon: Package, title: "Packaging", desc: "Design labels and source containers" },
  { num: 6, icon: MapPin, title: "Co-manufacturing", desc: "Find and vet production partners" },
  { num: 7, icon: ShieldCheck, title: "Regulatory", desc: "Navigate FDA, labeling, and compliance" },
  { num: 8, icon: Rocket, title: "Launch", desc: "Go to market with confidence" },
];

const CATEGORIES = [
  "Dry seasonings & spice blends",
  "Hot sauces, BBQ & simmer sauces",
  "Refrigerated dips & spreads",
  "Fresh salsa & pico",
  "Dressings & marinades",
  "Frozen meals & sides",
  "Savory snacks & mixes",
  "Meal kits",
];

const FREE_PREVIEW_TEMPLATE_ID = "savory-profile-intake";

const TEMPLATES = [
  { title: "Savory Profile Intake", locked: false, previewId: FREE_PREVIEW_TEMPLATE_ID },
  { title: "Commercial Formula Method Sheet", locked: true },
  { title: "First-Run Costing Worksheet", locked: true },
  { title: "Pricing & Margin Workbook", locked: true },
  { title: "Co-Man Outreach Email", locked: true },
  { title: "NDA & IP Checklist", locked: true },
  { title: "Testing Plan Worksheet", locked: true },
  { title: "Label & Regulatory Checklist", locked: true },
  { title: "Launch Readiness Scorecard", locked: true },
];

const PLAN_FEATURES = ["Full roadmap", "All templates", "Category-specific guidance", "Updates included"];
const PENDING_SELECTION_KEY = "alaine_compass_checkout_v1";
const PLAN_ORDER = ["compass_annual", "compass_guides_annual"];
const APPROVED_PLAN_NAMES = {
  compass_annual: "Individual",
  compass_guides_annual: "Compass + 3 Digital Guides",
};
const INTAKE_PATH = "/alaine-co/project-intake";

function readPendingSelection(user) {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PENDING_SELECTION_KEY) || "null");
    if (!parsed || !PLAN_ORDER.includes(parsed.tierKey)) return null;
    if (parsed.ownerId && (!user?.id || String(parsed.ownerId) !== String(user.id))) {
      sessionStorage.removeItem(PENDING_SELECTION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePendingSelection(selection) {
  try { sessionStorage.setItem(PENDING_SELECTION_KEY, JSON.stringify(selection)); } catch { /* URL return still works */ }
}

function formatPrice(amount) {
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(Number(amount) / 100);
}

export default function CompassStorefront() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busyPlan, setBusyPlan] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState("");
  const pending = useMemo(() => readPendingSelection(user), [user?.id]);
  const [selectedPlan, setSelectedPlan] = useState(pending?.tierKey || "compass_guides_annual");
  const [selectedGuides, setSelectedGuides] = useState(pending?.guideIds || []);

  useEffect(() => {
    let active = true;
    api.get("/compass/catalog", { skipNetToast: true })
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data?.plans) || !Array.isArray(data?.guides)) {
          throw new Error("Invalid Compass catalog");
        }
        setCatalog(data);
        setSelectedGuides((current) => {
          const available = new Set(data.guides.map((guide) => guide.id));
          const valid = current.filter((id) => available.has(id));
          return valid.length ? valid.slice(0, 3) : data.guides.slice(0, 3).map((guide) => guide.id);
        });
        setCatalogError("");
      })
      .catch(() => {
        if (active) setCatalogError("Pricing is temporarily unavailable. Please refresh or contact Alaine & Co.");
      });
    return () => { active = false; };
  }, []);

  // Links such as the public demo's "Get Compass" arrive at /compass#compass-pricing.
  useEffect(() => {
    if (window.location.hash === "#compass-pricing") {
      document.getElementById("compass-pricing")?.scrollIntoView?.({ behavior: "smooth" });
    }
  }, [catalog]);

  useEffect(() => {
    if (!catalog) return;
    savePendingSelection({
      tierKey: selectedPlan,
      guideIds: selectedPlan === "compass_guides_annual" ? selectedGuides : [],
      ...(user?.id ? { ownerId: user.id } : {}),
    });
  }, [catalog, selectedPlan, selectedGuides, user?.id]);

  const openFreePreview = (previewId) => {
    const t = COMPASS_TEMPLATES.find((x) => x.id === previewId);
    if (t) setPreviewTemplate(t);
  };

  const selectPlan = (tierKey) => {
    setSelectedPlan(tierKey);
    savePendingSelection({
      tierKey,
      guideIds: tierKey === "compass_guides_annual" ? selectedGuides : [],
      ...(user?.id ? { ownerId: user.id } : {}),
    });
  };

  const toggleGuide = (guideId) => {
    setSelectedGuides((current) => (
      current.includes(guideId)
        ? current.filter((id) => id !== guideId)
        : current.length < 3 ? [...current, guideId] : current
    ));
  };

  const startCheckout = async (tierKey = selectedPlan) => {
    const guideIds = tierKey === "compass_guides_annual" ? selectedGuides : [];
    selectPlan(tierKey);
    if (!user) {
      navigate(`/register?next=compass`);
      return;
    }
    if (tierKey === "compass_guides_annual" && guideIds.length !== 3) {
      toast.error("Choose 3 digital guides before checkout.");
      return;
    }
    setBusyPlan(tierKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier_key: tierKey,
        origin_url: window.location.origin,
         guide_ids: guideIds,
      });
      if (data?.url) {
        try { sessionStorage.removeItem(PENDING_SELECTION_KEY); } catch { /* no-op */ }
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout — please try again.");
        setBusyPlan(null);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Checkout failed");
      setBusyPlan(null);
    }
  };

  return (
    <div className="platform-blue min-h-[100dvh] bg-stone-50 text-stone-900" data-testid="compass-storefront">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <CompassIcon className="h-5 w-5 text-emerald-800" />
            <span className="font-semibold text-stone-900">
              Alaine's Compass<sup className="text-xs">™</sup>
            </span>
            <span className="hidden sm:inline text-stone-400">·</span>
            <span className="hidden sm:inline text-sm text-stone-500">by Alaine & Co.</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/dashboard" className="text-sm font-medium text-stone-600 transition-colors hover:text-emerald-800">
                My account
              </Link>
            ) : (
              <Link to="/login?next=compass" className="text-sm font-medium text-stone-600 transition-colors hover:text-emerald-800" data-testid="compass-signin">
                Sign in
              </Link>
            )}
            <button
              onClick={() => document.getElementById("compass-pricing")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-emerald-900"
              data-testid="compass-header-cta"
            >
              Get Compass
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-6 pb-24 pt-20">
        <div className="text-center">
          <p className="mb-6 text-xs font-semibold uppercase tracking-widest text-amber-700">
            Go-to-market toolkit
          </p>
          <h1 className="mb-6 text-5xl font-bold leading-tight tracking-tight text-stone-900 md:text-6xl">
            The roadmap from recipe to retail — built by a food scientist.
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-stone-600">
            Made for founders who are <em>not</em> food scientists. Get the templates, checklists, and step-by-step guidance to take your product from kitchen to shelf.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <button
              onClick={() => document.getElementById("compass-pricing")?.scrollIntoView({ behavior: "smooth" })}
              className="flex items-center gap-2 rounded-xl bg-emerald-800 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-emerald-900/20 transition-all hover:bg-emerald-900 hover:shadow-xl"
              data-testid="compass-hero-cta"
            >
              Choose your Compass plan
              <ArrowRight className="h-5 w-5" />
            </button>
            <a
              href="#roadmap"
              className="flex items-center gap-2 rounded-xl border-2 border-stone-200 bg-white px-8 py-4 text-lg font-semibold text-stone-700 transition-all hover:border-amber-600 hover:text-amber-700"
            >
              See what's inside ↓
            </a>            <Link
              to="/compass/demo"
              className="flex items-center gap-2 rounded-xl border-2 border-emerald-800 bg-white px-8 py-4 text-lg font-semibold text-emerald-800 transition-all hover:bg-emerald-50"
              data-testid="link-try-demo"
            >
              Try interactive demo
            </Link>
          </div>
          <div className="mt-4 flex justify-center">
            <CopyDemoLinkButton />
          </div>
          <p className="mt-8 text-sm text-stone-500">
            Guided commercialization from Alaine & Co.
          </p>
        </div>
      </section>

      {/* 8-Phase Roadmap */}
      <section id="roadmap" className="border-y border-stone-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-amber-700">
              Your path forward
            </p>
            <h2 className="text-3xl font-bold text-stone-900">The 8-phase roadmap</h2>
          </div>

          <div className="relative">
            <div className="absolute left-0 right-0 hidden h-1 bg-gradient-to-r from-emerald-800 via-amber-600 to-emerald-800 md:block" style={{ top: "3rem" }} />
            <div className="grid gap-8 md:grid-cols-4">
              {PHASES.map((phase) => {
                const Icon = phase.icon;
                return (
                  <div key={phase.num} className="relative flex flex-col items-center text-center">
                    <div className="relative z-10 mb-4 flex h-24 w-24 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-emerald-800 to-emerald-700 shadow-lg">
                      <Icon className="h-10 w-10 text-white" strokeWidth={1.5} />
                    </div>
                    <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-stone-900">
                      {phase.title}
                    </h3>
                    <p className="text-sm leading-snug text-stone-600">{phase.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section id="compass-pricing" className="bg-stone-50 py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-amber-700">
              Category-specific guidance
            </p>
            <h2 className="text-3xl font-bold text-stone-900">Made for your product</h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((category) => (
              <div
                key={category}
                className="group rounded-xl border border-stone-200 bg-white p-6 shadow-sm transition-all hover:border-amber-600 hover:shadow-md"
              >
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-800 transition-colors group-hover:bg-amber-50 group-hover:text-amber-700">
                  <Check className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <p className="font-semibold leading-snug text-stone-900">{category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates & Worksheets (locked preview) */}
      <section className="border-t border-stone-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-amber-700">
              The paid part
            </p>
            <h2 className="mb-4 text-3xl font-bold text-stone-900">Templates & worksheets</h2>
            <p className="mx-auto max-w-2xl text-stone-600">
              Fillable in your browser, downloadable, saved as you go.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="compass-locked-templates">
            {TEMPLATES.map((template) => (
              <div
                key={template.title}
                onClick={template.locked ? undefined : () => openFreePreview(template.previewId)}
                role={template.locked ? undefined : "button"}
                tabIndex={template.locked ? undefined : 0}
                onKeyDown={template.locked ? undefined : (e) => { if (e.key === "Enter" || e.key === " ") openFreePreview(template.previewId); }}
                data-testid={template.locked ? undefined : "compass-free-preview-card"}
                className={`group relative overflow-hidden rounded-xl border-2 p-6 transition-all ${
                  template.locked
                    ? "border-stone-200 bg-stone-50"
                    : "border-amber-600 bg-gradient-to-br from-amber-50 to-white shadow-lg shadow-amber-900/10 cursor-pointer hover:shadow-xl"
                }`}
              >
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm">
                    {template.locked ? (
                      <Lock className="h-6 w-6 text-stone-400" strokeWidth={2} />
                    ) : (
                      <FileText className="h-6 w-6 text-amber-700" strokeWidth={2} />
                    )}
                  </div>
                  {!template.locked && (
                    <span className="rounded-full bg-amber-700 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                      Free preview
                    </span>
                  )}
                </div>
                <h3 className="font-bold leading-snug text-stone-900">{template.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-stone-50 py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="mb-12 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-amber-700">
              Simple pricing
            </p>
            <h2 className="text-3xl font-bold text-stone-900">Get full access</h2>
          </div>

          {catalogError && (
            <div role="alert" className="mb-8 rounded-xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">
              {catalogError}
            </div>
          )}
          {!catalog && !catalogError && <p className="text-center text-stone-500">Loading current plans…</p>}
          {catalog && <div className="grid gap-6 md:grid-cols-2">
            {PLAN_ORDER.map((tierKey) => {
              const plan = catalog.plans.find((item) => item.key === tierKey);
              if (!plan) return null;
              const isGuideBundle = tierKey === "compass_guides_annual";
              const selected = selectedPlan === tierKey;
              return (
            <div key={tierKey} className={`relative rounded-2xl border-2 bg-white p-8 transition-all ${selected ? "border-amber-600 shadow-lg" : "border-stone-200 shadow-sm hover:border-emerald-800"}`}>
              <div className="mb-6">
                <h3 className="mb-2 text-lg font-bold uppercase tracking-wide text-stone-900">
                  {APPROVED_PLAN_NAMES[tierKey] || plan.name}
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-5xl font-black text-emerald-800">{formatPrice(plan.amount)}</span>
                  <span className="text-stone-500">/{plan.interval}</span>
                </div>
              </div>
              <ul className="mb-8 space-y-3">
                {PLAN_FEATURES.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-stone-700">
                    <Check className="h-5 w-5 flex-shrink-0 text-emerald-800" strokeWidth={2.5} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {isGuideBundle && (
                <div className="mb-6 rounded-xl border border-stone-200 bg-stone-50 p-4" data-testid="compass-guide-catalog">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="font-bold text-stone-900">Choose 3 digital guides</h4>
                    <span className="text-xs font-semibold text-emerald-800">{selectedGuides.length} of 3 selected</span>
                  </div>
                  <p className="mt-1 text-xs text-stone-500">{catalog.guide_count} guides are currently available; all 3 are selectable.</p>
                  <div className="mt-3 space-y-3">
                    {catalog.guides.map((guide) => (
                      <label key={guide.id} className="flex cursor-pointer items-start gap-3 rounded-lg bg-white p-3">
                        <input type="checkbox" checked={selectedGuides.includes(guide.id)} onChange={() => toggleGuide(guide.id)}
                          className="mt-1 accent-emerald-800" data-testid={`guide-choice-${guide.id}`} />
                        <span><span className="block text-sm font-semibold text-stone-900">{guide.title}</span>
                          <span className="block text-xs leading-relaxed text-stone-500">{guide.description}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <button
                onClick={() => { selectPlan(tierKey); startCheckout(tierKey); }}
                disabled={busyPlan !== null}
                className="w-full rounded-xl bg-emerald-800 py-4 font-semibold text-white transition-all hover:bg-emerald-900 disabled:opacity-60"
                data-testid={`compass-buy-${tierKey}`}
              >
                {busyPlan === tierKey ? "Opening checkout…" : `Choose ${APPROVED_PLAN_NAMES[tierKey] || plan.name}`}
              </button>
            </div>
              );
            })}
          </div>}

          <p className="mt-8 text-center text-sm text-stone-600" data-testid="compass-included-note">
            Already have Compass access through an Alaine & Co. engagement?{" "}
            <Link to="/login?next=compass" className="font-semibold text-emerald-800 hover:underline">sign in</Link> to open it.
          </p>
        </div>
      </section>

      <section className="border-t border-stone-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-10 text-center">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-amber-700">For partner organizations</p>
            <h2 className="text-3xl font-bold">Bring Compass to your founder community</h2>
            <p className="mx-auto mt-3 max-w-2xl text-stone-600">Partner access is coordinated through Alaine & Co. These plans do not include automated seat management.</p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              ["Partner", "$1,500/year", "10 founder seats · 2 staff seats · one location", "Additional founder seats: $99/year", "partner"],
              ["Partner Plus", "$2,500/year", "25 founder seats · 3 staff seats · one location", "Additional founder seats: $79/year", "partner-plus"],
              ["Founding Partner", "$1,200 first year", "First 5 approved partners", "Then $1,500 annually. Apply to inquire; availability is not automatic.", "founding-partner"],
              ["Enterprise", "Custom", "For larger or multi-location programs", "Scope and pricing by inquiry.", "enterprise"],
            ].map(([name, price, detail, note, purpose]) => (
              <div key={name} className="flex flex-col rounded-2xl border border-stone-200 p-6">
                <h3 className="font-bold text-stone-900">{name}</h3>
                <div className="mt-2 text-2xl font-black text-emerald-800">{price}</div>
                <p className="mt-4 text-sm text-stone-700">{detail}</p>
                <p className="mt-2 flex-1 text-xs leading-relaxed text-stone-500">{note}</p>
                <a href={`${INTAKE_PATH}?purpose=compass-${purpose}`} className="mt-5 rounded-lg bg-emerald-800 px-4 py-3 text-center text-sm font-semibold text-white">
                  {purpose === "founding-partner" ? "Apply / inquire" : "Partner inquiry"}
                </a>
              </div>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
            <div><h3 className="font-bold text-stone-900">Printed guide set</h3>
              <p className="mt-1 text-sm text-stone-700">$149 retail · $99 for active subscribers. Fulfillment is not currently available; inquire to plan an order.</p></div>
            <a href={`${INTAKE_PATH}?purpose=compass-printed-guides`} className="mt-4 inline-flex shrink-0 rounded-lg border border-amber-700 px-5 py-3 text-sm font-semibold text-amber-900 sm:mt-0">Printed guide inquiry</a>
          </div>
          <p className="mt-6 text-center text-sm text-stone-500">Consulting is a separate service. Alaine's Bench is internal-only and is not included with Compass.</p>
        </div>
      </section>

      {/* Consulting remains separate from the Compass product. */}
      <section className="bg-emerald-800 py-16 text-white">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="mb-4 text-2xl font-bold">Need hands-on technical support?</h2>
          <p className="mb-8 text-lg leading-relaxed text-emerald-50">
            Alaine & Co. consulting is a separate service. Alaine's Bench is an internal-only system and is not included with Compass.
          </p>
          <a
            href={`${INTAKE_PATH}?purpose=compass-consulting`}
            className="inline-block rounded-xl border-2 border-white bg-transparent px-8 py-4 font-semibold text-white transition-all hover:bg-white hover:text-emerald-800"
            data-testid="compass-explore-bench"
          >
            Consulting inquiry
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white py-8">
        <div className="mx-auto max-w-7xl px-6 text-center text-sm text-stone-500">
          <p>
            © Alaine & Co. · <Link to="/terms" className="hover:text-emerald-800">Terms</Link> ·{" "}
            <Link to="/privacy" className="hover:text-emerald-800">Privacy</Link>
          </p>
        </div>
      </footer>

      {previewTemplate && (
        <TemplateModal template={previewTemplate} onClose={() => setPreviewTemplate(null)} />
      )}
    </div>
  );
}
